import React, { useState, useEffect } from 'react';
import { Card, Table, DatePicker, Select, Button, Space, message, Typography, InputNumber } from 'antd';
import { ReloadOutlined, CalculatorOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { apiClient } from '../../../config/apiClient';
import MainLayout from '../../layout/layout';
const { Text } = Typography;

const Ganancias = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [rangoFechas, setRangoFechas] = useState([dayjs().startOf('month'), dayjs()]);
  const [categoriaId, setCategoriaId] = useState(undefined);
  const [pctGanancia, setPctGanancia] = useState(null);
  /** { pct, monto } tras Calcular / Actualizar con % válido */
  const [resultadoPorcentaje, setResultadoPorcentaje] = useState(null);
  /** La tabla y el resumen solo se muestran tras el primer «Calcular» */
  const [resultadosVisibles, setResultadosVisibles] = useState(false);

  const loadCategorias = async () => {
    try {
      const res = await apiClient.get('/gestion/categorias-articulos');
      if (res.ok) {
        const list = await res.json();
        setCategorias(Array.isArray(list) ? list : []);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const loadResumen = async () => {
    if (!rangoFechas || !rangoFechas[0] || !rangoFechas[1]) {
      message.warning('Seleccione el rango de fechas');
      return [];
    }
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('fecha_desde', rangoFechas[0].format('YYYY-MM-DD'));
      params.append('fecha_hasta', rangoFechas[1].format('YYYY-MM-DD'));
      if (categoriaId != null) {
        params.append('categoria_id', categoriaId);
      }
      const res = await apiClient.get(`/gestion/ganancias/resumen?${params.toString()}`);
      if (res.ok) {
        const rows = await res.json();
        const list = Array.isArray(rows) ? rows : [];
        setData(list);
        return list;
      }
      const err = await res.json().catch(() => ({}));
      message.error(err.message || 'Error al cargar ganancias');
      setData([]);
      return [];
    } catch (e) {
      message.error(e.message || 'Error al cargar ganancias');
      setData([]);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const totalGananciaDesdeRows = (rows) =>
    rows.reduce((s, r) => s + (Number(r.ganancia_total) || 0), 0);

  /** Aplica % sobre la ganancia acumulada estimada: monto = total × (pct / 100) */
  const aplicarPorcentaje = (rows, pctValor) => {
    const total = totalGananciaDesdeRows(rows);
    if (pctValor == null || pctValor === '') {
      setResultadoPorcentaje(null);
      return;
    }
    const pct = Number(pctValor);
    if (Number.isNaN(pct)) {
      setResultadoPorcentaje(null);
      return;
    }
    setResultadoPorcentaje({ pct, monto: total * (pct / 100) });
  };

  const handleCalcular = async () => {
    const rows = await loadResumen();
    setResultadosVisibles(true);
    if (pctGanancia == null || pctGanancia === '') {
      message.warning('Ingrese un porcentaje de ganancia');
      setResultadoPorcentaje(null);
      return;
    }
    aplicarPorcentaje(rows, pctGanancia);
  };

  const handleActualizar = async () => {
    if (!resultadosVisibles) return;
    const rows = await loadResumen();
    aplicarPorcentaje(rows, pctGanancia);
  };

  useEffect(() => {
    loadCategorias();
  }, []);

  const fmtMoney = (v) =>
    `$${Number(v || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const totalGanancia = data.reduce((s, r) => s + (Number(r.ganancia_total) || 0), 0);
  const totalCantidad = data.reduce((s, r) => s + (Number(r.cantidad) || 0), 0);
  const totalVentas = data.reduce(
    (s, r) => s + ((Number(r.cantidad) || 0) * (Number(r.art_precio_venta) || 0)),
    0
  );

  const columns = [
    {
      title: 'Artículo',
      dataIndex: 'art_nombre',
      key: 'art_nombre',
      ellipsis: true,
    },
    {
      title: 'Cantidad vendida',
      dataIndex: 'cantidad',
      key: 'cantidad',
      width: 130,
      align: 'right',
      render: (c) => Number(c).toLocaleString('es-AR', { maximumFractionDigits: 2 }),
    },
    {
      title: 'Precio compra',
      dataIndex: 'art_precio_compra',
      key: 'art_precio_compra',
      width: 130,
      align: 'right',
      render: (v) => fmtMoney(v),
    },
    {
      title: 'Precio venta',
      dataIndex: 'art_precio_venta',
      key: 'art_precio_venta',
      width: 130,
      align: 'right',
      render: (v) => fmtMoney(v),
    },
    {
      title: 'Ganancia (unit.)',
      dataIndex: 'ganancia',
      key: 'ganancia',
      width: 130,
      align: 'right',
      render: (v) => (
        <Text style={{ color: Number(v) >= 0 ? '#52c41a' : '#ff4d4f' }}>{fmtMoney(v)}</Text>
      ),
    },
    {
      title: 'Ganancia total',
      key: 'ganancia_total',
      width: 140,
      align: 'right',
      render: (_, record) => (
        <Text strong style={{ color: Number(record.ganancia_total) >= 0 ? '#52c41a' : '#ff4d4f' }}>
          {fmtMoney(record.ganancia_total)}
        </Text>
      ),
    },
    {
      title: 'Total ventas',
      key: 'total_ventas',
      width: 140,
      align: 'right',
      render: (_, record) => {
        const totalVentas = (Number(record.cantidad) || 0) * (Number(record.art_precio_venta) || 0);
        return <Text strong>{fmtMoney(totalVentas)}</Text>;
      },
    },
  ];

  return (
    <MainLayout>
      <div style={{ padding: 24 }}>
        <h1 style={{ margin: '0 0 16px', color: '#fff', fontSize: 24 }}>Ganancias</h1>
        <Text type="secondary" style={{ display: 'block', marginBottom: 16, color: '#b0b0b0' }}>
          Suma de unidades vendidas por artículo en el período. Precios según ficha del artículo;
          ganancia = precio venta − precio compra (por unidad). Ganancia total = cantidad × ganancia
          unitaria.
        </Text>

        <Card
          style={{ background: '#2d2d2d', borderColor: '#434343', marginBottom: 16 }}
          styles={{ body: { padding: 16 } }}
        >
          <Space wrap size="middle" align="end">
            <div>
              <div style={{ color: '#fff', marginBottom: 4, fontSize: 13 }}>Desde — Hasta</div>
              <DatePicker.RangePicker
                value={rangoFechas}
                onChange={(d) => setRangoFechas(d)}
                format="DD/MM/YYYY"
                allowClear={false}
              />
            </div>
            <div>
              <div style={{ color: '#fff', marginBottom: 4, fontSize: 13 }}>Categoría</div>
              <Select
                allowClear
                placeholder="Todas"
                style={{ minWidth: 220 }}
                value={categoriaId}
                onChange={setCategoriaId}
                options={categorias.map((c) => ({
                  value: c.cat_art_id,
                  label: c.cat_art_nombre,
                }))}
              />
            </div>
            <div>
              <div style={{ color: '#fff', marginBottom: 4, fontSize: 13 }}>% ganancia</div>
              <InputNumber
                min={0}
                max={100}
                placeholder="ej. 25"
                value={pctGanancia}
                onChange={setPctGanancia}
                style={{ width: 120 }}
                addonAfter="%"
              />
            </div>
            <Button type="primary" icon={<CalculatorOutlined />} onClick={handleCalcular} loading={loading}>
              Calcular
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleActualizar}
              loading={loading}
              disabled={!resultadosVisibles}
              title={!resultadosVisibles ? 'Pulse Calcular primero para ver el resumen' : undefined}
            >
              Actualizar
            </Button>
          </Space>
        </Card>

        {resultadosVisibles && (
          <Card style={{ background: '#2d2d2d', borderColor: '#434343' }} styles={{ body: { padding: 0 } }}>
            <div
              style={{
                padding: '12px 16px',
                borderBottom: '1px solid #434343',
                display: 'flex',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 12,
                alignItems: 'center',
              }}
            >
              <Text style={{ color: '#b0b0b0' }}>
                Artículos distintos: <strong style={{ color: '#fff' }}>{data.length}</strong>
                {' · '}
                Unidades totales: <strong style={{ color: '#fff' }}>{totalCantidad.toLocaleString('es-AR', { maximumFractionDigits: 2 })}</strong>
              </Text>
              <Space direction="vertical" align="end" size={4} style={{ textAlign: 'right' }}>
                <Text style={{ color: '#52c41a', fontSize: 16 }}>
                  Ganancia acumulada: <strong>{fmtMoney(totalGanancia)}</strong>
                </Text>
                {resultadoPorcentaje != null && (
                  <Text style={{ color: '#bae637', fontSize: 16 }}>
                    A ganar según {resultadoPorcentaje.pct}%: <strong>{fmtMoney(resultadoPorcentaje.monto)}</strong>
                  </Text>
                )}
                <Text style={{ color: '#fff', fontSize: 16 }}>
                  Total ventas: <strong>{fmtMoney(totalVentas)}</strong>
                </Text>
              </Space>
            </div>
            <Table
              rowKey="art_id"
              loading={loading}
              dataSource={data}
              columns={columns}
              pagination={{ pageSize: 15, showSizeChanger: true }}
              scroll={{ x: 900 }}
            />
          </Card>
        )}
      </div>
    </MainLayout>
  );
};

export default Ganancias;
